import _taggedTemplateLiteral from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";

var _templateObject, _templateObject2;

import NextLink from 'next/link';
import Image from 'next/legacy/image';
import styled from 'styled-components';
import { useTranslation } from '@yoweb/next-i18next';
import { media, pxToRem } from '@yoweb/ui/styles/utils';
import { th } from '@yoweb/ui/styles/utils/theme';
import { Box } from '@yoweb/ui/components/Box';
import { Flex } from '@yoweb/ui/components/Flex';
import { Grid } from '@yoweb/ui/components/Grid';
import { Text } from '@yoweb/ui/components/typography/Text';
import { Title } from '@yoweb/ui/components/typography/Title';
import { Button } from '@yoweb/ui/components/buttons/Button';
import { InsetLink } from '@poplar/components/InsetLink';
import { CallCard } from '@poplar/components/CallCard';
import { PAGE_SUBSCRIPTION } from '@poplar/constants/routes';
import { URL_CHECKOUT_PLAN_DIRECT, URL_FAQ_PAGE, URL_HOW_IT_WORKS_PAGE, URL_TUTORIAL_LANDING_PAGE } from '@poplar/constants/urls';
import { useExternalConfig } from '@poplar/contexts/ExternalConfig';
import { useIntlMeta } from '@yoweb/ui/hooks/useIntlMeta';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var useBannerImage = function useBannerImage() {
  var _useIntlMeta = useIntlMeta(),
      lcLocale = _useIntlMeta.lcLocale;

  if (lcLocale === 'ja-jp') {
    return '/images/illustrations/person-pointing-jpn.svg';
  }

  return '/images/illustrations/person-pointing.svg';
};

var useHowItWorks = function useHowItWorks() {
  var _useIntlMeta2 = useIntlMeta(),
      lcLocale = _useIntlMeta2.lcLocale;

  var _useExternalConfig = useExternalConfig(),
      baseStoreUrl = _useExternalConfig.baseStoreUrl;

  if (lcLocale === 'ja-jp') {
    return URL_TUTORIAL_LANDING_PAGE(baseStoreUrl);
  }

  return URL_HOW_IT_WORKS_PAGE(baseStoreUrl);
};

export var FooterBannerUnsubscribed = function FooterBannerUnsubscribed() {
  var _useTranslation = useTranslation('home'),
      t = _useTranslation.t;

  var bannerImage = useBannerImage();
  var howItWorksUrl = useHowItWorks();

  var _useExternalConfig2 = useExternalConfig(),
      baseStoreUrl = _useExternalConfig2.baseStoreUrl,
      globalFeatures = _useExternalConfig2.globalFeatures;

  var subscriptionLink = globalFeatures.enableOnboarding2_2 ? URL_CHECKOUT_PLAN_DIRECT(baseStoreUrl) : PAGE_SUBSCRIPTION;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CallCard, {
      p: "none",
      overflow: "hidden",
      my: "normal2",
      hoverable: true,
      children: /*#__PURE__*/_jsxs(BannerGrid, {
        flow: {
          _: 'row',
          lg: 'column'
        },
        columns: {
          _: 'auto',
          md: "".concat(pxToRem(250), " 1fr"),
          lg: "".concat(pxToRem(346), " 1fr")
        },
        gap: "none",
        "data-testid": "home-footer-unsubscribed-view",
        children: [/*#__PURE__*/_jsx(NextLink, {
          href: subscriptionLink,
          passHref: true,
          legacyBehavior: true,
          children: /*#__PURE__*/_jsx(InsetLink, {})
        }), /*#__PURE__*/_jsx(ImageWrapper, {
          children: /*#__PURE__*/_jsx(Image, {
            src: bannerImage,
            objectFit: "contain",
            objectPosition: "bottom left",
            layout: "fill"
          })
        }), /*#__PURE__*/_jsxs(BodyWrapper, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsxs(ContentGrid, {
            gap: "small3",
            children: [/*#__PURE__*/_jsx(Title, {
              size: "sm",
              children: t('footer.unsubscribed.title')
            }), /*#__PURE__*/_jsx(Text, {
              children: t('footer.unsubscribed.description')
            })]
          }), /*#__PURE__*/_jsx(NextLink, {
            href: subscriptionLink,
            passHref: true,
            legacyBehavior: true,
            children: /*#__PURE__*/_jsx(Button, {
              as: "a",
              children: t('footer.unsubscribed.subscribe-link-text')
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsxs(Box, {
      my: "medium2",
      children: [/*#__PURE__*/_jsx(Title, {
        textAlign: "center",
        size: "sm",
        children: t('learn-more-title')
      }), /*#__PURE__*/_jsxs(Box, {
        display: "flex",
        justifyContent: "center",
        gap: "normal1",
        mt: "normal3",
        children: [/*#__PURE__*/_jsx(NextLink, {
          href: howItWorksUrl,
          passHref: true,
          legacyBehavior: true,
          children: /*#__PURE__*/_jsx(Button, {
            as: "a",
            target: "_blank",
            "data-testid": "home-footer-unsubscribed-view-how-it-works",
            children: t('how-it-works')
          })
        }), /*#__PURE__*/_jsx(NextLink, {
          href: URL_FAQ_PAGE(baseStoreUrl),
          passHref: true,
          legacyBehavior: true,
          children: /*#__PURE__*/_jsx(Button, {
            as: "a",
            variant: "secondary",
            target: "_blank",
            children: t('faq')
          })
        })]
      })]
    })]
  });
};
var ContentGrid = styled(Grid).withConfig({
  componentId: "sc-15p2igj-0"
})(["&&{max-width:", ";}"], pxToRem(400));
var BodyWrapper = styled(Flex).withConfig({
  componentId: "sc-15p2igj-1"
})(["&&{background:", ";margin:3rem;align-items:center;text-align:center;justify-content:center;gap:", ";", "}"], th.getColor('base000'), th.getSpace('normal3'), media.md(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      text-align: left;\n      align-items: flex-start;\n    "]))));
var BannerGrid = styled(Grid).withConfig({
  componentId: "sc-15p2igj-2"
})(["&&{min-height:", ";grid-auto-rows:1fr 1fr;", "}"], pxToRem(255), media.md(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      grid-auto-rows: auto;\n    "]))));
var ImageWrapper = styled(Flex).withConfig({
  componentId: "sc-15p2igj-3"
})(["&&{background:", ";justify-content:flex-start;position:relative;}"], th.getColor('gingko'));